/**
 * Distance Calculator Utility
 * Google Maps Distance Matrix APIを使用した正確な移動時間計算
 */

import { Location } from '../types/core';

export interface DistanceResult {
    distance: number; // メートル
    duration: number; // 分
    status: 'OK' | 'NOT_FOUND' | 'ZERO_RESULTS' | 'ERROR';
}

/**
 * 2点間の直線距離を計算（Haversine公式）
 * @param from 出発地点
 * @param to 到着地点
 * @returns 距離（メートル）
 */
export function calculateStraightLineDistance(from: Location, to: Location): number {
    const R = 6371000; // 地球の半径（メートル）
    const lat1Rad = (from.lat * Math.PI) / 180;
    const lat2Rad = (to.lat * Math.PI) / 180;
    const deltaLatRad = ((to.lat - from.lat) * Math.PI) / 180;
    const deltaLngRad = ((to.lng - from.lng) * Math.PI) / 180;

    const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) *
        Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}

/**
 * 直線距離から徒歩時間を推定
 * @param distance 距離（メートル）
 * @returns 徒歩時間（分）
 */
export function estimateWalkingTime(distance: number): number {
    // 徒歩速度: 4.0km/h = 50m/min（現実的な徒歩速度）
    const walkingSpeedMeterPerMin = 50;

    // 道路の迂回率を考慮（直線距離の1.3倍）
    const detourFactor = 1.3;

    const actualDistance = distance * detourFactor;
    const walkingTime = Math.ceil(actualDistance / walkingSpeedMeterPerMin);

    // 最低1分、最大30分でキャップ
    return Math.max(1, Math.min(30, walkingTime));
}

/**
 * Google Maps Distance Matrix APIを使用した正確な移動時間計算
 * 注意: 本来はサーバーサイドで実行すべき（APIキーの保護のため）
 * @param from 出発地点
 * @param to 到着地点
 * @param apiKey Google Maps API Key（オプション）
 * @returns 移動時間と距離の情報
 */
export async function calculateAccurateDistance(
    from: Location,
    to: Location,
    apiKey?: string
): Promise<DistanceResult> {
    // APIキーがない場合は推定値を返す
    if (!apiKey) {
        const distance = calculateStraightLineDistance(from, to);
        const duration = estimateWalkingTime(distance);

        return {
            distance: Math.round(distance * 1.3), // 迂回率を考慮
            duration,
            status: 'OK'
        };
    }

    try {
        const origins = `${from.lat},${from.lng}`;
        const destinations = `${to.lat},${to.lng}`;

        const url = `https://maps.googleapis.com/maps/api/distancematrix/json?` +
            `origins=${origins}&destinations=${destinations}&mode=walking&` +
            `language=ja&units=metric&key=${apiKey}`;

        const response = await fetch(url);
        const data = await response.json() as any;

        if (data.status === 'OK' && data.rows?.[0]?.elements?.[0]?.status === 'OK') {
            const element = data.rows[0].elements[0];
            return {
                distance: element.distance.value,
                duration: Math.ceil(element.duration.value / 60), // 秒を分に変換
                status: 'OK'
            };
        } else {
            // APIエラーの場合は推定値にフォールバック
            const distance = calculateStraightLineDistance(from, to);
            const duration = estimateWalkingTime(distance);

            return {
                distance: Math.round(distance * 1.3),
                duration,
                status: 'ERROR'
            };
        }
    } catch (error) {
        console.warn('Distance Matrix API error, falling back to estimation:', error);

        // エラーの場合は推定値にフォールバック
        const distance = calculateStraightLineDistance(from, to);
        const duration = estimateWalkingTime(distance);

        return {
            distance: Math.round(distance * 1.3),
            duration,
            status: 'ERROR'
        };
    }
}

/**
 * 複数地点間の移動時間を一括計算
 * @param locations 地点のリスト
 * @param apiKey Google Maps API Key（オプション）
 * @returns 各区間の移動時間と距離
 */
export async function calculateRouteDistances(
    locations: Location[],
    apiKey?: string
): Promise<DistanceResult[]> {
    if (locations.length < 2) {
        return [];
    }

    const results: DistanceResult[] = [];

    for (let i = 0; i < locations.length - 1; i++) {
        const from = locations[i];
        const to = locations[i + 1];

        const result = await calculateAccurateDistance(from, to, apiKey);
        results.push(result);

        // API制限を考慮して少し待機
        if (apiKey && i < locations.length - 2) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    return results;
}

/**
 * 移動時間の表示用フォーマット
 * @param minutes 分数
 * @returns フォーマットされた文字列
 */
export function formatWalkingTime(minutes: number): string {
    if (minutes < 1) {
        return '1分未満';
    } else if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (remainingMinutes === 0) {
            return `${hours}時間`;
        } else {
            return `${hours}時間${remainingMinutes}分`;
        }
    } else {
        return `${minutes}分`;
    }
}

export default {
    calculateStraightLineDistance,
    estimateWalkingTime,
    calculateAccurateDistance,
    calculateRouteDistances,
    formatWalkingTime
};